/* ---------------------------------------------------------------------------
   What to do about a defect, and which budget it comes out of.

   `QC-05`. Two sources, doing two different jobs.

   **DoR decides the CLASS.** Programme Appraisal Document BIMP Table 3.3,
   reproduced in DoR's Interactive Bridge Toolkit, sorts all bridge maintenance
   into Routine, Minor, Major and Emergency, each with a diagnostic. That
   classification is the load-bearing part of this module, because it decides
   what the application is allowed to put a price on:

     Routine    No significant defect; cyclic servicing. Priced from the interim
                norms. **The BMS generates no priority list for routine work** —
                it is budgeted per division through the ARMP.
     Minor      "Damages detected are very minor and do not require any design
                input to carry out the treatment or rectify the defects."
                → costable from norms and district rates.
     Major      "Defects of structural and hydraulic significance affecting the
                function of the bridge. Such defects might require detailed
                investigation, design and cost estimation for maintenance works."
                → NOT costable here. The honest output is that it needs design.
     Emergency  "One or more elements of the bridge have been heavily and
                critically damaged, and safety of the traffic using the bridge is
                at stake." → load restriction and signs first.

   The refusal in the Major row is the most important line in this file. An
   application that produced a confident figure for a pier repair — a thing DoR's
   own programme document says requires detailed investigation and design before
   it can be estimated — would be inventing the one number the whole process
   exists to produce carefully. Refusing to price it is not a gap in the feature.
   It is the feature agreeing with the procedure.

   **Standards decide the METHOD.** Which treatment suits a defect of a given
   size is ACI 546R and EN 1504-9 practice, not a DoR publication: crack coating
   below ~0.2 mm, resin injection from ~0.2 to 1 mm, filling above 1 mm; patch
   repair for spalling and delamination without active corrosion; replacement
   where section loss is past economical repair.

   THOSE BANDS ARE NOT APPENDIX D's, AND ARE NOT RECONCILED WITH THEM. Appendix D
   rates a crack Good below 0.3 mm and Fair to 1 mm; the repair standards switch
   method at 0.2 mm and 1 mm. They are answering different questions — one
   assigns a condition, the other selects a material — and averaging them into
   one set of numbers would produce a scale that is neither. Both are carried,
   each cited to what published it.
   --------------------------------------------------------------------------- */

import type { DefectQuantity } from './types.ts';
import type { QuantityKey } from './quantities.ts';

export type MaintenanceClass = 'Routine' | 'Minor' | 'Major' | 'Emergency';

export const MAINTENANCE_CLASSES: readonly MaintenanceClass[] = [
  'Routine',
  'Minor',
  'Major',
  'Emergency',
];

/** BIMP Table 3.3's own diagnostic for each class, quoted where it is quotable. */
export const CLASS_DIAGNOSTIC: Record<MaintenanceClass, string> = {
  Routine:
    'No significant defect and no replacement or repair of any element of the bridge is required. Servicing, in a cyclic manner.',
  Minor:
    'Damages detected are very minor and do not require any design input to carry out the treatment or rectify the defects.',
  Major:
    'Defects of structural and hydraulic significance affecting the function of the bridge. Such defects might require detailed investigation, design and cost estimation for maintenance works.',
  Emergency:
    'One or more elements of the bridge have been heavily and critically damaged, and safety of the traffic using the bridge is at stake.',
};

/**
 * Whether this application may put a figure on work of this class.
 *
 * Routine and Minor: yes, from norms and district rates. Major and Emergency:
 * no, and the reason is in the diagnostic above rather than in a limitation of
 * this code — DoR's own programme document says the estimate comes after an
 * investigation and a design.
 */
export function isCostable(k: MaintenanceClass): boolean {
  return k === 'Routine' || k === 'Minor';
}

/** Who plans and procures work of each class, per BIMP Table 3.3 and the Toolkit. */
export const CLASS_ROUTE: Record<MaintenanceClass, string> = {
  Routine:
    'Division, budgeted through the Annual Road Maintenance Plan against approved norms. No BMS priority list is generated for routine maintenance.',
  Minor:
    'The BMS generates the priority list, which is the basis for annual programming and budgeting through the ARMP. Carried out by contract.',
  Major:
    'The BMS generates the priority list. Consultants prepare design, drawings and cost estimates; contractors implement under Division supervision.',
  Emergency:
    'Load restriction and traffic signs first, assessed by Special Inspection. Design procured afterwards, then civil works.',
};

export interface Treatment {
  /** Stable id. Stored on a costed work item, so never change one without a migration. */
  id: string;
  /** The work item, in the words a bill of quantities would use. */
  label: string;
  klass: MaintenanceClass;
  /**
   * The measurement that supplies the amount, or null.
   *
   * Null where the work is not proportional to anything the inspector measured —
   * an investigation is an investigation whatever the crack width. A treatment
   * with no quantity key can never be costed by the quantity path, which is
   * consistent: every one of them is Major.
   */
  quantityKey: QuantityKey | null;
  /** Unit of measure the work is billed in. Named, never implied. */
  unit: string;
  /** What says this is the right treatment at this size. */
  source: string;
  /** One line on what the work is and when it is the wrong answer. */
  note: string;
}

/**
 * One rule: a defect category, optionally a size band, and what it calls for.
 *
 * Rules are evaluated in order and the FIRST match wins, so bands are written
 * narrowest-first within a category. A category's last rule has no `when` and is
 * its fallback — which means every category always produces a treatment, and a
 * defect with no measurement at all still gets one rather than falling through
 * to nothing.
 */
export interface TreatmentRule {
  code: string;
  when?: (q: DefectQuantity | null) => boolean;
  treatment: Treatment;
}

const ACI = 'ACI 546R / EN 1504-9 repair method selection';
const BIMP = 'Programme Appraisal Document BIMP Table 3.3';

/** A measurement, or null when it was not taken. Keeps the rule predicates readable. */
const m = (q: DefectQuantity | null, key: QuantityKey): number | null => {
  if (!q || q.method === 'not-measured') return null;
  const v = q[key];
  return typeof v === 'number' ? v : null;
};

export const TREATMENT_RULES: readonly TreatmentRule[] = [
  /* ---------------- cracking ---------------- */
  {
    code: 'CRK',
    when: (q) => {
      const w = m(q, 'widthMm');
      return w !== null && w <= 0.2;
    },
    treatment: {
      id: 'crk-surface-seal',
      label: 'Surface coating / crack sealing to fine cracks',
      klass: 'Minor',
      quantityKey: 'lengthM',
      unit: 'm',
      source: ACI,
      note: 'Protects the cover rather than restoring it. Wrong where the crack is moving — a sealed live crack reopens through the coating.',
    },
  },
  {
    code: 'CRK',
    when: (q) => {
      const w = m(q, 'widthMm');
      return w !== null && w <= 1;
    },
    treatment: {
      id: 'crk-resin-injection',
      label: 'Resin injection to cracks 0.2–1 mm',
      klass: 'Minor',
      quantityKey: 'lengthM',
      unit: 'm',
      source: ACI,
      note: 'Restores monolithic action where the crack is dormant. A live crack needs a flexible sealant instead.',
    },
  },
  {
    code: 'CRK',
    treatment: {
      id: 'crk-investigate',
      label: 'Investigate cause of cracking, then fill',
      klass: 'Major',
      quantityKey: null,
      unit: '—',
      source: `${ACI}; class per ${BIMP}`,
      note: 'Above 1 mm the width is a symptom. Filling it without establishing the cause treats the evidence rather than the defect — and an unmeasured crack lands here for the same reason: nobody has established how wide it is.',
    },
  },

  /* ---------------- spalling and scaling ---------------- */
  {
    code: 'SPA',
    treatment: {
      id: 'spa-patch-partial',
      label: 'Partial-depth patch repair to spalled concrete',
      klass: 'Minor',
      quantityKey: 'areaM2',
      unit: 'm²',
      source: ACI,
      note: 'For surface deterioration without active corrosion. Where reinforcement is exposed and rusting, log it as REE — that is a different repair and a different class.',
    },
  },
  {
    code: 'SCA',
    treatment: {
      id: 'sca-surface-treatment',
      label: 'Surface treatment to scaled concrete',
      klass: 'Minor',
      quantityKey: 'areaM2',
      unit: 'm²',
      source: ACI,
      note: 'Removes loose material and reinstates the surface. Does not restore section.',
    },
  },

  /* ---------------- exposed reinforcement ---------------- */
  {
    code: 'REE',
    when: (q) => {
      const loss = m(q, 'sectionLossPct');
      return loss !== null && loss > 20;
    },
    treatment: {
      id: 'ree-replace',
      label: 'Full-depth replacement — section loss beyond economical repair',
      klass: 'Major',
      quantityKey: null,
      unit: '—',
      source: `${ACI}; class per ${BIMP}`,
      note: 'Past roughly 20% loss the bar is no longer the bar that was designed. Capacity has to be reassessed, which is design work.',
    },
  },
  {
    code: 'REE',
    treatment: {
      id: 'ree-patch-and-treat',
      label: 'Break out, clean and treat reinforcement, reinstate cover',
      klass: 'Major',
      quantityKey: null,
      unit: '—',
      source: `${ACI}; class per ${BIMP}`,
      note: 'Exposed and rusting reinforcement is active corrosion in a structural member — Appendix D rates it Poor on sight. Major whatever the area, because the extent of loss behind the cover is not visible until it is broken out.',
    },
  },

  /* ---------------- potholes ---------------- */
  {
    code: 'POH',
    treatment: {
      id: 'poh-surfacing-patch',
      label: 'Cut out and patch bridge deck surfacing',
      klass: 'Minor',
      quantityKey: 'areaM2',
      unit: 'm²',
      source: `${BIMP} — approach slabs and surfacing rectification`,
      note: 'Surfacing only. A pothole that goes through to the structural deck is a deck defect, not a surfacing one.',
    },
  },

  /* ---------------- joints ---------------- */
  {
    code: 'SEP',
    when: (q) => {
      const gap = m(q, 'widthMm');
      return gap !== null && gap <= 25;
    },
    treatment: {
      id: 'sep-reseal',
      label: 'Clean out and reseal expansion joint',
      klass: 'Minor',
      quantityKey: 'lengthM',
      unit: 'm',
      source: `${BIMP} — rectification of bridge expansion joints`,
      note: 'Where the joint itself is sound and the sealant is not. DoR Part 3 §5.1 already has cleaning it out as routine work.',
    },
  },
  {
    code: 'SEP',
    treatment: {
      id: 'sep-replace',
      label: 'Replace expansion joint',
      klass: 'Minor',
      quantityKey: 'lengthM',
      unit: 'm',
      source: `${BIMP} — "Rectification and replacement of bridge expansion joints"`,
      note: 'Named in Table 3.3 as minor maintenance explicitly, which is why a joint replacement is costable here while a bearing replacement is not.',
    },
  },

  /* ---------------- scour ---------------- */
  {
    code: 'SCO',
    when: (q) => {
      const d = m(q, 'depthM');
      return d !== null && d <= 1;
    },
    treatment: {
      id: 'sco-bed-protection',
      label: 'Bed protection to keep the foundation unexposed',
      klass: 'Minor',
      quantityKey: 'volumeM3',
      unit: 'm³',
      source: `${BIMP} — "Minor bed protection to minimize scour required to keep the foundations unexposed"`,
      note: 'Table 3.3 admits this as minor work only while the foundation is still covered. Once it is exposed the question is the foundation, not the bed.',
    },
  },
  {
    code: 'SCO',
    treatment: {
      id: 'sco-investigate',
      label: 'Investigate foundation, design scour protection',
      klass: 'Major',
      quantityKey: null,
      unit: '—',
      source: `${BIMP} — hydraulic significance`,
      note: 'Scour past a metre, or unmeasured, is the failure mode that takes bridges out in this country. It is a hydraulic defect affecting the function of the structure, which is Table 3.3’s definition of major.',
    },
  },

  /* ---------------- settlement ---------------- */
  {
    code: 'SET',
    treatment: {
      id: 'set-investigate',
      label: 'Monitor movement, investigate cause',
      klass: 'Major',
      quantityKey: null,
      unit: '—',
      source: `${BIMP} — structural significance`,
      note: 'The manual’s own instruction is to monitor the RATE of movement in order to judge urgency. A single settlement reading is a starting point, not a repair.',
    },
  },

  /* ---------------- erosion ---------------- */
  {
    code: 'ERO',
    when: (q) => {
      const v = m(q, 'volumeM3');
      return v !== null && v <= 200;
    },
    treatment: {
      id: 'ero-bank-protection',
      label: 'Gabion or rip-rap bank protection',
      klass: 'Minor',
      quantityKey: 'volumeM3',
      unit: 'm³',
      source: `${BIMP} — "bank protection in order to prevent breaching of approach road"`,
      note: 'The example §4.3 of the inspection manual gives for why quantities are worth recording at all, in the unit it gives it in.',
    },
  },
  {
    code: 'ERO',
    treatment: {
      id: 'ero-training-works',
      label: 'Design river training works',
      klass: 'Major',
      quantityKey: null,
      unit: '—',
      source: `${BIMP} — hydraulic significance`,
      note: 'Past a couple of hundred cubic metres this is river engineering rather than bank protection, and putting gabions where the river is going to move is money spent twice.',
    },
  },
];

/**
 * The treatment for one defect, or null for a category with no rule.
 *
 * First match wins, and every category's last rule is unconditional — so this
 * returns null only for a defect code that has no rules at all, which today is
 * none of the nine. It is nullable anyway because a code added to
 * `DEFECT_CODES` without a rule here should produce "no treatment recorded"
 * rather than silently inherit another category's.
 */
export function treatmentFor(
  code: string,
  q: DefectQuantity | null,
  wording?: TreatmentWording,
): Treatment | null {
  for (const rule of TREATMENT_RULES) {
    if (rule.code !== code) continue;
    if (rule.when && !rule.when(q)) continue;
    return worded(rule.treatment, wording);
  }
  return null;
}

/* --------------------------- the department's wording --------------------------- */

/**
 * A treatment reworded by the administrator — migration 0047.
 *
 * Only the words. The class, the unit and the rule that picks the treatment
 * stay the ones above: the class decides whether the register may price the
 * work and the unit is what a district rate is checked against.
 */
export interface StoredWording {
  treatment: string;
  label: string;
  note: string;
  editedBy: string | null;
  editedAt: string;
}

/** The reworded treatments, by id. */
export type TreatmentWording = ReadonlyMap<string, StoredWording>;

export function wordingMap(rows: readonly StoredWording[]): TreatmentWording {
  return new Map(rows.map((w) => [w.treatment, w]));
}

/** A treatment in the department's wording, where it has one. */
export function worded(t: Treatment, wording?: TreatmentWording): Treatment {
  const w = wording?.get(t.id);
  return w ? { ...t, label: w.label, note: w.note } : t;
}

/** Whether an id names a treatment one of the rules above can recommend. */
export function isTreatmentId(id: string): boolean {
  return TREATMENT_RULES.some((r) => r.treatment.id === id);
}

/* --------------------------- routine servicing --------------------------- */

/**
 * Cyclic work, per element, from *Part 3 — Guidelines for Routine Maintenance*.
 *
 * NOT DEFECT-DRIVEN, which is the whole distinction Table 3.3 draws: routine
 * maintenance is what is done when there is *no significant defect*. So these
 * hang off the element rather than off a finding, and they are what the
 * Division's ARMP submission is built from.
 *
 * Part 3 §1.2 is worth reading before adding to this list: routine maintenance
 * is "bridge servicing rather than bridge repair", and §1.3 says explicitly that
 * it "does not cover the renewal of structural elements or components". Anything
 * that renews something belongs in the rules above, not here.
 */
export interface RoutineOperation {
  /** Element id from src/data/elements.ts. */
  element: string;
  /** The operations, as Part 3 lists them. */
  operations: readonly string[];
  /** How often, in Part 3's own words. */
  interval: string;
  /** When in the year, where Part 3 says. */
  timing?: string;
  source: string;
}

export const ROUTINE_OPERATIONS: readonly RoutineOperation[] = [
  {
    element: 'river_training',
    operations: [
      'Remove accumulation of debris and vegetation, especially tree trunks and branches, around and between piers and abutments',
      'Note new erosion and scour, deposition of river bed material, and change of river direction',
      'Note any extraction of sand or gravel within 200 m upstream or downstream',
      'Clear any encroachments in the area of the bridge site',
    ],
    interval: '3–4 times a year',
    timing:
      'Before the monsoon (July); in the dry season after the monsoon when rivers are low (October); after large floods',
    source: 'Guidelines for Inspection and Maintenance of Bridges, Vol. 1, Part 3, §2',
  },
  {
    element: 'pier',
    operations: [
      'Remove graffiti and any vegetation from the structure',
      'Clear debris including animal and fecal deposits from bearing shelves and bridge surfaces',
      'Clean drainage channels; clean out drainage pipes and remove silt and debris, and test on completion',
    ],
    interval: '3–4 times a year',
    timing:
      'Drains cleared after leaf fall and working before the monsoon starts (July); vegetation cleared before the growing season (April)',
    source: 'Guidelines for Inspection and Maintenance of Bridges, Vol. 1, Part 3, §3',
  },
  {
    element: 'abutment',
    operations: [
      'Remove graffiti and any vegetation from the structure',
      'Clean out weep pipes and remove silt and debris; clean outlet pipes and test on completion',
      'Remove all debris including animal and fecal deposits and clean drainage channels',
    ],
    interval: '3–4 times a year',
    timing:
      'Drains cleared after leaf fall and working before the monsoon starts (July); vegetation cleared before the growing season (April)',
    source: 'Guidelines for Inspection and Maintenance of Bridges, Vol. 1, Part 3, §3.4',
  },
  {
    element: 'deck',
    operations: [
      'Remove graffiti and any vegetation from the structure',
      'Clear debris and stones including animal and fecal deposits from bridge deck and surfaces',
      'Clean drainage channels; clear drainage outlet pipes and test on completion; clear weep pipes',
      'Check all bolts and nuts for tightness, and report missing or loose bolts',
    ],
    interval: '3–4 times a year',
    timing:
      'Drains cleared after leaf fall and working before the monsoon starts (July); vegetation cleared before the growing season (April)',
    source: 'Guidelines for Inspection and Maintenance of Bridges, Vol. 1, Part 3, §4',
  },
  {
    element: 'deck_surface',
    operations: [
      'Clear debris and stones from the deck surface and footpath',
      'Clean drainage channels and clear drainage spouts on the deck',
      'Report cracks, deformation and potholes in bridge deck surfacing',
    ],
    interval: '3–4 times a year',
    source: 'Guidelines for Inspection and Maintenance of Bridges, Vol. 1, Part 3, §4.1',
  },
  {
    element: 'joints',
    operations: [
      'Clean out stones, debris and vegetation from the joint',
      'Check the condition of joint sealant and report damaged or missing gap sealant',
      'Clear drainage systems; check all bolts and nuts for tightness',
      'Replace gaskets where the specific maintenance manual requires it',
    ],
    interval: '3–4 times a year',
    source: 'Guidelines for Inspection and Maintenance of Bridges, Vol. 1, Part 3, §5.1',
  },
  {
    element: 'parapet',
    operations: [
      'Check all bolts and nuts for tightness and report loose or missing bolts',
      'Clear hollow section drain holes',
      'Remove graffiti, vegetation and debris',
    ],
    interval: '3–4 times a year',
    source: 'Guidelines for Inspection and Maintenance of Bridges, Vol. 1, Part 3, §5.3',
  },
  {
    element: 'bearings',
    operations: [
      'Remove any vegetation from the bearing shelf and plinth',
      'Clear debris and stones including animal and fecal deposits from the shelf and plinth',
      'Elastomeric: remove general debris and dirt; report tearing, bulging, flattening or other abnormal deformation',
      'Sliding and roller: clean contact surfaces where accessible and re-grease per the manufacturer’s instructions',
    ],
    interval: '3–4 times a year',
    source: 'Guidelines for Inspection and Maintenance of Bridges, Vol. 1, Part 3, §5.5–5.10',
  },
  {
    element: 'protection',
    operations: [
      'Clean surfaces of debris and animal contamination',
      'Report damage, corrosion and breakdown of the protective system',
    ],
    interval: '3–4 times a year',
    source: 'Guidelines for Inspection and Maintenance of Bridges, Vol. 1, Part 3, §6',
  },
];

/** The routine servicing due on one element, or null where Part 3 prescribes none. */
export function routineFor(elementId: string): RoutineOperation | null {
  return ROUTINE_OPERATIONS.find((r) => r.element === elementId) ?? null;
}
